import { SlideablePanel } from "@/components/ui/SlideablePanel";
import { OpenInDataCurationLink } from "@/features/data-curation/components/OpenInDataCurationLink";
import { IS_DEV } from "@/features/config";
import { useFeatureFlags } from "@/features/config/useFeatureFlags";
import { datasetsAccess } from "@/features/datasets/routes";
import { closeLeftSidebarAtom, leftSidebarOpenAtom } from "@/utils/sidebarStore";
import { useAtom, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChatSession } from "../context/ChatSessionProvider";
import { useCitations } from "../context/CitationProvider";
import { extractCitations } from "../utils/citations";
import { CitationSidebar } from "./CitationSidebar";
import { MessageList } from "./MessageList";
import { PromptInput, PromptInputHandle } from "./PromptInput";
import { useIsChatHome } from "../hooks/useIsChatHome";
import { cn } from "@/utils/cn";
import { latestExtractionDatasetId } from "../utils/extractionDataset";

const NAV_HEIGHT = "3.5rem"; // h-14 — matches AppShell NavBar for right-panel offset

export const ChatLayout = () => {
  const inputRef = useRef<PromptInputHandle>(null);
  const { selectedCitation, setCitations, closeCitation } = useCitations();
  const { messages, sessionId, traceTurns } = useChatSession();
  const { flags } = useFeatureFlags();
  const closeLeftSidebar = useSetAtom(closeLeftSidebarAtom);
  const isHome = useIsChatHome();

  // ── Citation extraction (session-wide for export / download / save only).
  // In-text click resolution uses per-response-group citations in MessageList.

  const prevSessionRef = useRef(sessionId);
  useEffect(() => {
    if (prevSessionRef.current !== sessionId) {
      prevSessionRef.current = sessionId;
      setCitations([]);
    }
  }, [sessionId, setCitations]);

  const allAssistantContent = useMemo(() => {
    return messages
      .filter((m) => m.role === "assistant" && m.content)
      .map((m) => m.content!)
      .join("\n");
  }, [messages]);

  useEffect(() => {
    if (!allAssistantContent.trim()) return;

    const extracted = extractCitations(allAssistantContent);
    if (extracted.length > 0) setCitations(extracted);
  }, [allAssistantContent, setCitations]);

  const prevSessionIdRef = useRef(sessionId);
  useEffect(() => {
    const wasEmpty = prevSessionIdRef.current === undefined;
    const isNowSession = sessionId !== undefined;
    if (wasEmpty && isNowSession) {
      inputRef.current?.setText("");
    }
    prevSessionIdRef.current = sessionId;
  }, [sessionId]);

  const handleExampleClick = useCallback((query: string) => {
    inputRef.current?.setText(query);
    inputRef.current?.focus();
  }, []);

  // A conversation that creates an old (curator) dataset links to it: on
  // /datasets, or read-only on /datasets/legacy where the new Datasets replace it.
  const datasets = datasetsAccess(flags, IS_DEV);
  const dataCurationEnabled = datasets.curator !== null;
  const extractionDatasetId = useMemo(
    () =>
      dataCurationEnabled
        ? latestExtractionDatasetId(traceTurns)
        : null,
    [dataCurationEnabled, traceTurns],
  );

  // ── Deferred close: keep content visible during the 300ms slide-out ──

  const [panelOpen, setPanelOpen] = useState(false);
  const [citationPanelWidth, setCitationPanelWidth] = useState(512);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [isLeftSidebarOpen] = useAtom(leftSidebarOpenAtom);

  useEffect(() => {
    if (selectedCitation) {
      clearTimeout(closeTimeoutRef.current);
      setPanelOpen(true);
      closeLeftSidebar();
    }
  }, [closeLeftSidebar, selectedCitation]);

  useEffect(() => {
    return () => clearTimeout(closeTimeoutRef.current);
  }, []);

  const handleClosePanel = useCallback(() => {
    setPanelOpen(false);
    closeTimeoutRef.current = setTimeout(() => {
      closeCitation();
    }, 300);
  }, [closeCitation]);

  useEffect(() => {
    if (isLeftSidebarOpen) {
      handleClosePanel();
    }
  }, [handleClosePanel, isLeftSidebarOpen]);

  // AppShell owns NavBar + left rail; this layout only fills main + citation panel.
  return (
    <div className="flex h-full min-h-0 flex-1 overflow-hidden">
      {/* Main chat area. On the home state the masthead and the prompt are
          one composition, centred in the page (biased slightly upward); the
          column itself scrolls if the viewport is short. Once a conversation
          exists the list fills the space and the prompt docks to the bottom. */}
      <div
        className={cn(
          "relative flex min-h-0 min-w-0 flex-1 flex-col",
          isHome ? "overflow-y-auto overscroll-contain" : "overflow-hidden",
        )}
      >
        {extractionDatasetId && (
          <div className="absolute right-3 top-3 z-10 flex justify-end">
            <OpenInDataCurationLink datasetId={extractionDatasetId} access={datasets} />
          </div>
        )}
        <div className={cn("shrink-0", isHome ? "flex-[2] min-h-4" : "hidden")} aria-hidden />
        <MessageList onExampleClick={handleExampleClick} />
        <PromptInput ref={inputRef} />
        <div className={cn("shrink-0", isHome ? "flex-[3] min-h-6" : "hidden")} aria-hidden />
      </div>

      {/* Contextual citation viewer */}
      <SlideablePanel
        isOpen={panelOpen}
        onClose={handleClosePanel}
        offsetTop={NAV_HEIGHT}
        from="right"
        width={`${citationPanelWidth}px`}
        alwaysOpenOnDesktop={false}
        ariaLabel="Citation viewer"
        resizability={{
          onWidthChange: setCitationPanelWidth,
          minWidth: 320,
          maxWidth: 800,
        }}
      >
        <CitationSidebar onClose={handleClosePanel} />
      </SlideablePanel>
    </div>
  );
};
